interface StyleConfig {
  key: string;
  title: string;
  description: string;
  additionalProps?: Record<string, any>;
  wrapper?: string;
  wrapperProps?: Record<string, any>;
}

interface StyleLayoutItem {
  gridSize: { xs: number; md?: number };
  components: StyleConfig[];
  wrapper?: string;
  wrapperProps?: Record<string, any>;
}

export const previewCallLevelSentimentsStyleLayout: StyleLayoutItem[] = [
  {
    gridSize: { xs: 12, md: 4 },
    components: [
      {
        key: 'call_quality_distribution',
        title: 'Call Quality Distribution',
        description: 'This pie chart displays the distribution of dominant call qualities during the selected time frame.',
        additionalProps: {
          height: 460,
          graphHeight: 350,
        }
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 5 },
    components: [
      {
        key: 'sentiment_trend',
        title: 'Sentiment Distribution Comparison',
        description: 'Period-over-period comparison of positive, neutral, and negative sentiment distribution',
        additionalProps: {
          dataKeys: ['positive', 'neutral', 'negative'],
          height: 460,
          graphHeight: 350,
          xAxisKey: 'date',
          colors: ['#8cee96ff', '#2b6ac2b5', '#e63418ff'],
          xAxisTitle: 'Period',
          yAxisTitle: 'Percentage (%)',
          showLegend: true
        }
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 3 },
    wrapper: 'Stack',
    wrapperProps: { spacing: 0.5, direction: 'column', sx: { height: '100%' } },
    components: [
      {
        key: 'calls_requiring_review.critical',
        title: 'Critical Calls',
        description: 'Calls requiring immediate attention',
        additionalProps: {
          compact: true,
          valueFontSize: '2rem',
          titleFontSize: '0.75rem',
          descriptionFontSize: '0.7rem'
        },
        wrapper: 'Box',
        wrapperProps: { sx: {} }
      },
      {
        key: 'calls_requiring_review.high',
        title: 'High Priority Calls',
        description: 'Review within 24 hours',
        additionalProps: {
          compact: true,
          valueFontSize: '2rem',
          titleFontSize: '0.75rem',
          descriptionFontSize: '0.7rem'
        },
        wrapper: 'Box',
        wrapperProps: { sx: {} }
      },
      {
        key: 'calls_requiring_review.medium',
        title: 'Medium Priority Calls',
        description: 'Review within 1 week',
        additionalProps: {
          compact: true,
          valueFontSize: '2rem',
          titleFontSize: '0.75rem',
          descriptionFontSize: '0.7rem'
        },
        wrapper: 'Box',
        wrapperProps: { sx: {} }
      },
    ]
  },
  {
    gridSize: { xs: 12, md: 4 },
    components: [
      {
        key: 'call_type_breakdown',
        title: 'Call Type Performance',
        description: 'This bar chart shows the distribution of call types within the selected date range.',
        additionalProps: {
          dataKeys: ['value'],
          height: 450,
          graphHeight: 350,
          xAxisKey: 'name',
          colors: ['#60fabaff'],
          xAxisTitle: 'Call Type',
          yAxisTitle: 'Number of Calls'
        },
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 5 },
    components: [
      {
        key: 'engagement_distribution',
        title: 'Engagement Distribution',
        description: 'This histogram shows the frequency distribution of engagement levels across ranges.',
        additionalProps: {
          dataKey: 'frequency',
          height: 450,
          graphHeight: 350,
          xAxisKey: 'range',
          color: '#3db4f4ba',
          xAxisTitle: 'Engagement Range',
          yAxisTitle: 'Frequency',
          barCategoryGap: '1%'
        },
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 3 },
    components: [
      {
        key: 'stress_detection',
        title: 'Stress Detection Summary',
        description: 'Multi-indicator stress detection across audio, video, and text channels',
        additionalProps: {
          height: 450
        }
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 12 },
    components: [
      {
        key: 'duration_quality',
        title: 'Duration vs Quality Analysis',
        description: 'Relationship between call duration and quality with quadrant classification',
        additionalProps: {
          height: 500,
          graphHeight: 350,
          xAxisKey: 'duration',
          yAxisKey: 'quality',
          quadrantKey: 'quadrant',
          tooltipKey: 'tooltip_text',
          xAxisTitle: 'Duration (minutes)',
          yAxisTitle: 'Quality Score',
          xAxisUnit: ' min',
          yAxisUnit: '',
          showZones: true,
          referenceLines: [
            { axis: 'y', value: 50, color: '#9CA3AF', label: 'Avg Quality' },
            { axis: 'x', value: 20, color: '#9CA3AF', label: 'Optimal Time' }
          ]
        }
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 6 },
    components: [
      {
        key: 'emotion_patterns',
        title: 'Emotions Distribution',
        description: 'Emotional patterns detected across all calls',
        additionalProps: {
          dataKey: 'frequency',
          height: 470,
          graphHeight: 350,
          xAxisKey: 'type',
          color: '#9fb2ebba',
          xAxisTitle: 'Emotions Type',
          yAxisTitle: 'Frequency',
          barCategoryGap: '1%'
        },
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 6 },
    components: [
      {
        key: 'talk_balance',
        title: 'Talk Time Balance (Host vs Participant)',
        description: 'Distribution of host talk time percentages across calls',
        additionalProps: {
          dataKey: 'frequency',
          xAxisKey: 'range',
          height: 470,
          graphHeight: 350,
          color: '#3df4f1ba',
          xAxisTitle: 'Engagement Range',
          yAxisTitle: 'Frequency',
          barCategoryGap: '1%'
        }
      }
    ]
  },
  {
    gridSize: { xs: 12, md: 12 },
    components: [
      {
        key: 'period_comparison',
        title: 'Period Comparison',
        description: 'Key metrics compared to previous period - track your progress over time',
        additionalProps: {
          height: 300
        }
      }
    ]
  },
];